import type { ApolloClient } from '@apollo/client';
import { from, map } from 'rxjs';
import { SwarmType } from '@/graphql/types';
import { ldenValuesQuery } from '../../../../../../graph/queries';
import { DATA_TYPE } from '../../../../../../graph/enums';
import { fixFloat, getDataType } from '../../../../../../graph/data-types';
import type { RuntimeDataServiceInput, RuntimeDataServiceOutput } from '../contracts';
import { toSoundMeasuringPointRelayId } from '../measuringPointInput';
import { RuntimeDataService } from '../types';
import type {
    RuntimeDataServiceRequest,
    RuntimeResultLinkDescriptorTabular,
    RuntimeTabularColumnDescriptor,
} from '../types';
import {
    isRecord,
    normalizeArrayEntries,
    normalizeNullableFiniteNumber,
    normalizeNullableInteger,
    normalizeNullableString,
    normalizeOrderedTimeWindow,
    normalizeRequiredId,
} from '../validationPrimitives';
import { formatTimeRange } from '../tabular';

interface LdenValuesQueryVariables {
    measuringPointId: string;
    startTime: number;
    endTime: number;
}

interface LdenValuesQueryData {
    ldenValues?: RuntimeDataServiceOutput<'measuring_point.lden_values'>;
}

type LdenValueEntry = RuntimeDataServiceOutput<'measuring_point.lden_values'>['values'][number];

function withLdenUnit(label: string): string {
    const { unit } = getDataType(DATA_TYPE.LDEN);
    return `${label} (${unit})`;
}

function formatCorrection(value: number | null | undefined): string {
    if (value === null || typeof value === 'undefined') {
        return '';
    }

    const formatted = fixFloat(DATA_TYPE.LDEN, value);
    return value > 0 ? `+${formatted}` : formatted;
}

function formatPeriod(entry: LdenValueEntry, timezone: string): string {
    const fromTimestamp = entry.timeSpan?.from;
    if (fromTimestamp === null || typeof fromTimestamp === 'undefined') {
        return '';
    }

    const toTimestamp = entry.timeSpan?.to;
    if (toTimestamp === null || typeof toTimestamp === 'undefined') {
        return '';
    }

    const scheduleName = entry.timeWindow?.name ? ` (${entry.timeWindow.name})` : '';
    return `${formatTimeRange(fromTimestamp, toTimestamp, timezone)}${scheduleName}`;
}

function formatStatus(entry: LdenValueEntry): string {
    if (entry.value === null || typeof entry.value === 'undefined') {
        return '';
    }

    const alarmLevel = entry.timeWindow?.alarmLevel;
    if (alarmLevel !== null && typeof alarmLevel !== 'undefined' && entry.value >= alarmLevel) {
        return gettext('ALARM');
    }

    const warningLevel = entry.timeWindow?.warningLevel;
    if (
        warningLevel !== null &&
        typeof warningLevel !== 'undefined' &&
        entry.value >= warningLevel
    ) {
        return gettext('WARNING');
    }

    return gettext('Complies');
}

function createLdenValueColumns() {
    return [
        {
            kind: 'text',
            key: 'period',
            requiresTimezone: true,
            label: gettext('Date/Time'),
            defaultAlignment: 'left',
            resolveCell: (entry: LdenValueEntry, options) => formatPeriod(entry, options.timezone),
        },
        {
            kind: 'measurement',
            key: 'value',
            label: gettext('DATA_SERVICE_LDEN_VALUES_VALUE_LABEL'),
            dataType: DATA_TYPE.LDEN,
            defaultAlignment: 'right',
            resolveCell: (entry: LdenValueEntry) => entry.value,
        },
        {
            kind: 'text',
            key: 'correction',
            label: withLdenUnit(gettext('DATA_SERVICE_LDEN_VALUES_CORRECTION_LABEL')),
            defaultAlignment: 'right',
            resolveCell: (entry: LdenValueEntry) => formatCorrection(entry.timeWindow?.correction),
        },
        {
            kind: 'measurement',
            key: 'warning_level',
            label: gettext('DATA_SERVICE_LDEN_VALUES_WARNING_LEVEL_LABEL'),
            dataType: DATA_TYPE.LDEN,
            defaultAlignment: 'right',
            resolveCell: (entry: LdenValueEntry) => entry.timeWindow?.warningLevel,
        },
        {
            kind: 'measurement',
            key: 'alarm_level',
            label: gettext('DATA_SERVICE_LDEN_VALUES_ALARM_LEVEL_LABEL'),
            dataType: DATA_TYPE.LDEN,
            defaultAlignment: 'right',
            resolveCell: (entry: LdenValueEntry) => entry.timeWindow?.alarmLevel,
        },
        {
            kind: 'text',
            key: 'status',
            label: gettext('STATUS'),
            defaultAlignment: 'left',
            resolveCell: (entry: LdenValueEntry) => formatStatus(entry),
        },
    ] as const satisfies readonly RuntimeTabularColumnDescriptor<LdenValueEntry>[];
}

function normalizeLdenScheduleWindow(
    value: unknown,
    pathLabel: string
): RuntimeDataServiceOutput<'measuring_point.lden_values'>['values'][number]['timeWindow'] {
    if (value === null || typeof value === 'undefined') {
        return null;
    }

    if (!isRecord(value)) {
        throw new Error(`Expected "${pathLabel}" to be an object or null.`);
    }

    return {
        startDay: normalizeNullableInteger(value.startDay, `${pathLabel}.startDay`),
        startTime: normalizeNullableInteger(value.startTime, `${pathLabel}.startTime`),
        endDay: normalizeNullableInteger(value.endDay, `${pathLabel}.endDay`),
        endTime: normalizeNullableInteger(value.endTime, `${pathLabel}.endTime`),
        name: normalizeNullableString(value.name, `${pathLabel}.name`),
        warningLevel: normalizeNullableFiniteNumber(
            value.warningLevel,
            `${pathLabel}.warningLevel`
        ),
        alarmLevel: normalizeNullableFiniteNumber(value.alarmLevel, `${pathLabel}.alarmLevel`),
        correction: normalizeNullableFiniteNumber(value.correction, `${pathLabel}.correction`),
    };
}

function normalizeRequiredLdenScheduleWindow(
    value: unknown,
    pathLabel: string
): RuntimeDataServiceOutput<'measuring_point.lden_values'>['settings'][number] {
    const normalized = normalizeLdenScheduleWindow(value, pathLabel);
    if (normalized === null) {
        throw new Error(`Expected "${pathLabel}" to be an object.`);
    }

    return normalized;
}

function normalizeLdenTimeSpan(
    value: unknown,
    pathLabel: string
): RuntimeDataServiceOutput<'measuring_point.lden_values'>['timeSpan'] {
    if (value === null || typeof value === 'undefined') {
        return null;
    }

    if (!isRecord(value)) {
        throw new Error(`Expected "${pathLabel}" to be an object or null.`);
    }

    return {
        from: normalizeNullableFiniteNumber(value.from, `${pathLabel}.from`),
        to: normalizeNullableFiniteNumber(value.to, `${pathLabel}.to`),
    };
}

function normalizeLdenValueEntry(
    value: unknown,
    pathLabel: string
): RuntimeDataServiceOutput<'measuring_point.lden_values'>['values'][number] {
    if (!isRecord(value)) {
        throw new Error(`Expected "${pathLabel}" to be an object.`);
    }

    return {
        timeWindow: normalizeLdenScheduleWindow(value.timeWindow, `${pathLabel}.timeWindow`),
        value: normalizeNullableFiniteNumber(value.value, `${pathLabel}.value`),
        timeSpan: normalizeLdenTimeSpan(value.timeSpan, `${pathLabel}.timeSpan`),
    };
}

function validateLdenValuesInput(
    input: unknown
): RuntimeDataServiceInput<'measuring_point.lden_values'> {
    if (!isRecord(input)) {
        throw new Error('Expected "measuring_point.lden_values" input to be an object.');
    }

    const { startTime, endTime } = normalizeOrderedTimeWindow(
        input.startTime,
        input.endTime,
        'measuring_point.lden_values'
    );

    return {
        measuringPointId: normalizeRequiredId(
            input.measuringPointId,
            'measuring_point.lden_values.measuringPointId'
        ),
        startTime,
        endTime,
    };
}

function validateLdenValuesResult(
    result: unknown
): RuntimeDataServiceOutput<'measuring_point.lden_values'> {
    // TODO(#9413): replace this generic fallback with explicit non-sound/cross-swarm
    // report-template compatibility handling.
    if (result === null || typeof result === 'undefined') {
        return {
            values: [],
            settings: [],
            timeSpan: null,
        };
    }

    if (!isRecord(result)) {
        throw new Error('Expected "measuring_point.lden_values" result to be an object.');
    }

    return {
        values: normalizeArrayEntries(
            result.values,
            'measuring_point.lden_values.values',
            normalizeLdenValueEntry,
            { allowNullish: true }
        ),
        settings: normalizeArrayEntries(
            result.settings,
            'measuring_point.lden_values.settings',
            normalizeRequiredLdenScheduleWindow,
            { allowNullish: true }
        ),
        timeSpan: normalizeLdenTimeSpan(result.timeSpan, 'measuring_point.lden_values.timeSpan'),
    };
}

function parseLdenValuesTableRows(linkValue: unknown): readonly LdenValueEntry[] {
    return validateLdenValuesResult({
        values: linkValue,
        settings: [],
        timeSpan: null,
    }).values;
}

export class LdenValuesRuntimeDataService extends RuntimeDataService<'measuring_point.lden_values'> {
    static readonly serviceId = 'measuring_point.lden_values' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_LDEN_VALUES_LABEL'),
            description: gettext('DATA_SERVICE_LDEN_VALUES_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        const ldenValueColumns = createLdenValueColumns();

        return [
            {
                key: 'values',
                label: gettext('DATA_SERVICE_LDEN_VALUES_VALUES_LABEL'),
                description: gettext('DATA_SERVICE_LDEN_VALUES_VALUES_DESCRIPTION'),
                supportedContextKinds: ['measuring-point'],
                supportedSwarmTypes: [SwarmType.Sound],
                path: 'values',
                valueKind: 'tabular',
                argPolicy: 'forbidden',
                exposeInInsertionPresets: true,
                columns: ldenValueColumns,
                parseTableRows: parseLdenValuesTableRows,
                buildInput: ({ context, graphTimeWindow }) => {
                    if (!context || context.kind !== 'measuring-point') {
                        throw new Error(gettext('DATA_SERVICE_LDEN_VALUES_ERROR_CONTEXT_REQUIRED'));
                    }

                    if (!graphTimeWindow) {
                        throw new Error(
                            gettext('DATA_SERVICE_LDEN_VALUES_ERROR_GRAPH_SETTINGS_REQUIRED')
                        );
                    }

                    return {
                        measuringPointId: context.measuringPoint.id,
                        startTime: graphTimeWindow.viewStartTs,
                        endTime: graphTimeWindow.viewEndTs,
                    };
                },
            },
        ] as const satisfies readonly RuntimeResultLinkDescriptorTabular<
            'measuring_point.lden_values',
            LdenValueEntry
        >[];
    }

    constructor(private readonly client: ApolloClient<object>) {
        super();
    }

    validateArgs = validateLdenValuesInput;

    validateResult = validateLdenValuesResult;

    createResult$ = ({ input }: RuntimeDataServiceRequest<'measuring_point.lden_values'>) =>
        from(
            this.client.query<LdenValuesQueryData, LdenValuesQueryVariables>({
                query: ldenValuesQuery,
                variables: {
                    measuringPointId: toSoundMeasuringPointRelayId(input.measuringPointId),
                    startTime: input.startTime,
                    endTime: input.endTime,
                },
                fetchPolicy: 'cache-first',
            })
        ).pipe(
            map(({ data }) => {
                if (!data || typeof data.ldenValues === 'undefined') {
                    // TODO(#9413): replace this missing-payload fallback with explicit
                    // non-sound/cross-swarm report-template compatibility handling.
                    return validateLdenValuesResult(null);
                }

                return validateLdenValuesResult(data.ldenValues);
            })
        );
}

export function createLdenValuesService(
    client: ApolloClient<object>
): LdenValuesRuntimeDataService {
    return new LdenValuesRuntimeDataService(client);
}
